import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Megaphone } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { WysiwygEditor } from './WysiwygEditor';
import { ConfirmModal } from './ConfirmModal';
import { useNotify } from './useNotify';

interface Notification {
  id: string;
  title: string;
  body_html: string;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
}

// timestamptz (ISO, UTC) <-> <input type="datetime-local"> (local, no tz)
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(v: string): string | null {
  return v ? new Date(v).toISOString() : null;
}

interface EditState {
  id: string | null;
  title: string;
  body_html: string;
  is_active: boolean;
  starts_at: string; // datetime-local
  ends_at: string;
}

const BLANK: EditState = { id: null, title: '', body_html: '', is_active: true, starts_at: '', ends_at: '' };

export function NotificationManagement() {
  const { notify, NotifyModal } = useNotify();
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await apiClient.get<Notification[]>('/api/notifications'));
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => setEdit({ ...BLANK });
  const openEdit = (n: Notification) =>
    setEdit({
      id: n.id,
      title: n.title,
      body_html: n.body_html,
      is_active: n.is_active,
      starts_at: isoToLocalInput(n.starts_at),
      ends_at: isoToLocalInput(n.ends_at),
    });

  const save = async () => {
    if (!edit) return;
    if (!edit.title.trim()) {
      notify('Notice', 'Please enter a title.', 'warning');
      return;
    }
    setSaving(true);
    const payload = {
      title: edit.title.trim(),
      body_html: edit.body_html,
      is_active: edit.is_active,
      starts_at: localInputToIso(edit.starts_at),
      ends_at: localInputToIso(edit.ends_at),
    };
    try {
      if (edit.id) await apiClient.put(`/api/notifications/${edit.id}`, payload);
      else await apiClient.post('/api/notifications', payload);
      setEdit(null);
      await load();
    } catch (err) {
      console.error('Failed to save notification:', err);
      notify('Error', 'Failed to save the notification. Please try again.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await apiClient.del(`/api/notifications/${deleteId}`);
      setDeleteId(null);
      await load();
    } catch (err) {
      console.error('Failed to delete notification:', err);
      notify('Error', 'Failed to delete the notification.', 'danger');
    }
  };

  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary flex items-center gap-2">
            <Megaphone size={24} /> Notifications
          </h1>
          <p className="text-sm text-theme-secondary">Broadcast announcements that appear as a banner to all users.</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          <Plus size={18} /> New Notification
        </button>
      </div>

      <div className="bg-surface rounded-xl border border-line overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-theme-secondary">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-theme-secondary">No notifications yet. Create one to broadcast a message.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-subtle text-left text-xs uppercase tracking-wide text-theme-secondary">
              <tr>
                <th className="px-4 py-2 font-semibold">Title</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold">Window</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {items.map((n) => (
                <tr key={n.id} className="hover:bg-subtle">
                  <td className="px-4 py-3 font-medium text-theme-primary">{n.title}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                        n.is_active ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300' : 'bg-subtle text-theme-secondary'
                      }`}
                    >
                      {n.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-theme-secondary">
                    {fmt(n.starts_at)} → {fmt(n.ends_at)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(n)} className="p-1.5 rounded text-theme-secondary hover:bg-subtle" title="Edit">
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => setDeleteId(n.id)} className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10" title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !saving && setEdit(null)}>
          <div className="bg-surface rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-line">
              <h2 className="text-base font-bold text-theme-primary">{edit.id ? 'Edit Notification' : 'New Notification'}</h2>
            </div>
            <div className="overflow-y-auto px-5 py-4 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-theme-primary mb-1">Title</label>
                <input
                  type="text"
                  value={edit.title}
                  onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                  className="w-full px-3 py-2 border border-line rounded-lg text-sm bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Short headline shown in the banner"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-theme-primary mb-1">Message</label>
                <WysiwygEditor
                  value={edit.body_html}
                  onChange={(html) => setEdit({ ...edit, body_html: html })}
                  placeholder="Write the announcement shown when a user opens it…"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-theme-primary mb-1">Show from</label>
                  <input
                    type="datetime-local"
                    value={edit.starts_at}
                    onChange={(e) => setEdit({ ...edit, starts_at: e.target.value })}
                    className="w-full px-3 py-2 border border-line rounded-lg text-sm bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                  <p className="text-[11px] text-theme-secondary mt-0.5">Blank = immediately</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-theme-primary mb-1">Show until</label>
                  <input
                    type="datetime-local"
                    value={edit.ends_at}
                    onChange={(e) => setEdit({ ...edit, ends_at: e.target.value })}
                    className="w-full px-3 py-2 border border-line rounded-lg text-sm bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                  <p className="text-[11px] text-theme-secondary mt-0.5">Blank = no end</p>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-theme-primary">
                <input
                  type="checkbox"
                  checked={edit.is_active}
                  onChange={(e) => setEdit({ ...edit, is_active: e.target.checked })}
                  className="h-4 w-4"
                />
                Active (uncheck to hide without deleting)
              </label>
            </div>
            <div className="px-5 py-3 border-t border-line flex justify-end gap-2">
              <button
                onClick={() => setEdit(null)}
                disabled={saving}
                className="px-4 py-2 rounded-lg border border-line text-theme-primary hover:bg-subtle text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteId !== null}
        title="Delete notification"
        message="This permanently removes the notification for everyone. Continue?"
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />

      {NotifyModal}
    </div>
  );
}
